// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useEffect } from 'react';

import { useEvolvePaginatedGet, type EvolveApiParams } from '@evolvemep/api-ts';
import { Stack, Text } from '@mantine/core';

import type { EntityState } from 'components/Mantine/StatusEditor/types';
import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';

import { SelectableShipmentCard } from './SelectableShipmentCard';

type Props = {
  state: EntityState<'Shipment'>;
  defaultParams: EvolveApiParams<'GET_PAGINATED', 'shop/shipment'>;
};

export const ShipmentSection = ({ state, defaultParams }: Props) => {
  const defaultOpts = useMemo(
    () => ({
      defaultConfig: {
        params: { shipmentStateIds: [state.stateId], ...defaultParams },
      },
    }),
    [defaultParams, state],
  );
  const {
    data: shipments,
    entireCount,
    loading,
    fetchNextPage,
    setDefaultOpts,
  } = useEvolvePaginatedGet('shop/shipment', defaultOpts);
  useEffect(() => {
    setDefaultOpts(defaultOpts);
  }, [defaultOpts, setDefaultOpts]);

  return (
    <Stack gap="xs">
      <Text c="dimmed" fw={600}>
        {state.name} {isNotNil(entireCount) && `(${entireCount})`}
      </Text>
      {shipments.map((shipment) => (
        <SelectableShipmentCard key={shipment.shipmentId} shipment={shipment} />
      ))}
      <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
    </Stack>
  );
};
